'use client';

import { cn, formatNumber } from '@ahm/common-helpers';
import {
  Avatar,
  Box,
  Card,
  Group,
  Progress,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import type { ProgressesSubscriptionData } from '../../api';
import stylesSubscription from '../subscription-styles.module.css';
import styles from './progress.module.css';
import ProgressesStatus from './progresses-status';
import { ROUTES } from '@/config/router';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type ProgressesItemProps = {
  data: ProgressesSubscriptionData;
};

export default function ProgressesItem({ data }: ProgressesItemProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const locale = useLocale();

  const handleClickSubscription = () => {
    router.push(`${ROUTES.mySubscriptionDetail(data._id)}`);
  };

  const percentVoucherUsed = Number((data.use_left * 100) / data.max_use);

  const formattedEndDate = dayjs(data.end_time * 1000).format('DD/MM/YYYY');

  return (
    <Card
      withBorder
      className={cn('relative cursor-pointer rounded-xl hover:shadow-4')}
      classNames={{
        root: stylesSubscription.cardSection,
      }}
      radius={rem(12)}
      m={0}
      w="100%"
      onClick={handleClickSubscription}
      pos="relative"
      pb={0}
      style={{
        overflow: 'visible',
      }}
    >
      <Card.Section>
        <Group
          justify="space-between"
          align="flex-start"
          gap={8}
          p={isMobile ? rem(8) : rem(12)}
          wrap="nowrap"
        >
          <Avatar
            style={{ flexShrink: 0 }}
            radius={rem(8)}
            src={data.icon_url}
            h={isMobile ? 88 : 104}
            w={isMobile ? 88 : 104}
            alt={data._id}
          >
            <Avatar
              src={`${assetPrefix}/static/images/sub-thumbnail.webp`}
              style={{ flexShrink: 0 }}
              radius={rem(8)}
              h={isMobile ? 88 : 104}
              w={isMobile ? 88 : 104}
              alt={data._id}
            />
          </Avatar>

          <Stack
            flex="auto"
            style={{ flexShrink: 0 }}
            align="stretch"
            justify="flex-start"
            gap={rem(4)}
          >
            <Title
              order={isMobile ? 6 : 4}
              lineClamp={1}
              maw="60%"
              c="secondary.7"
            >
              {data.title[locale]}
            </Title>
            {data.description[locale] && (
              <Text
                w="100%"
                size={isMobile ? 'xs' : 'md'}
                lh={isMobile ? rem(16) : rem(24)}
                fw={500}
                lineClamp={2}
                c="neutral.6"
              >
                {data.description[locale]}
              </Text>
            )}
            {data.end_time && (
              <Text size="sm" c="neutral.4" fw={500}>
                {data.is_auto_renew ? 'Tự động gia hạn vào' : 'Hết hạn vào'}{' '}
                {formattedEndDate}
              </Text>
            )}

            {data.use_left ? (
              <Stack gap={rem(4)} mt={rem(4)}>
                <Progress
                  value={percentVoucherUsed}
                  classNames={{
                    section:
                      percentVoucherUsed === 100 ? '' : styles.customProgress,
                  }}
                  styles={{
                    section: {
                      position: 'relative',
                      background:
                        'linear-gradient(to right, var(--mantine-color-primary-6), var(--mantine-color-primary-3))',
                    },
                  }}
                />

                <Text
                  size="xs"
                  fw={500}
                  c="neutral.6"
                  style={{ display: 'flex', alignItems: 'center', gap: rem(4) }}
                >
                  <span
                    style={{
                      color: 'var(--mantine-color-primary-7)',
                      fontSize: rem(14),
                      lineHeight: rem(20),
                      fontWeight: 600,
                    }}
                  >
                    Còn {formatNumber(data.use_left)}
                  </span>
                  / {formatNumber(data.max_use)} mã
                </Text>
              </Stack>
            ) : null}
          </Stack>
        </Group>
      </Card.Section>

      {data.label ? (
        <Box pos="absolute" top={rem(-4)} right={rem(-5)}>
          <ProgressesStatus label={data.label} />
        </Box>
      ) : null}
    </Card>
  );
}
